<div class="page" dir="ltr">
  <header class="hero">
    <p class="eyebrow">Catalog admin</p>
    <h1>Classification flow</h1>
    <p class="lede">
      Each voting company classifies the product. Two must agree on the same group and sub-group.
      A product with no admin label is saved. An admin label is left as it is, and the result says Match or Miss.
      Suggested new codes show on the card and wait under צריך אותי on the classification page.
    </p>
  </header>

  <div class="tabs" role="tablist">
    <button
      type="button"
      role="tab"
      class="tab"
      data-testid="classification-flow-tab-single"
      [class.active]="tab === 'single'"
      [attr.aria-selected]="tab === 'single'"
      [disabled]="running"
      (click)="setTab('single')"
    >
      Single product
    </button>
    <button
      type="button"
      role="tab"
      class="tab"
      data-testid="classification-flow-tab-scoreboard"
      [class.active]="tab === 'scoreboard'"
      [attr.aria-selected]="tab === 'scoreboard'"
      [disabled]="running"
      (click)="setTab('scoreboard')"
    >
      Scoreboard
    </button>
    <button
      type="button"
      role="tab"
      class="tab"
      data-testid="classification-flow-tab-unlabeled"
      [class.active]="tab === 'unlabeled'"
      [attr.aria-selected]="tab === 'unlabeled'"
      [disabled]="running"
      (click)="setTab('unlabeled')"
    >
      Unlabeled
    </button>
  </div>

  <p class="error" role="alert" *ngIf="errorMessage">{{ errorMessage }}</p>
  <p class="ok" role="status" *ngIf="statusMessage">{{ statusMessage }}</p>

  <label class="photo-skip card" data-testid="classification-flow-skip-photos">
    <input
      type="checkbox"
      name="skipPhotos"
      [(ngModel)]="skipPhotos"
      [disabled]="running"
    />
        <span>Don't send product photos (cheaper; text-only prompts)</span>
  </label>

  <ng-container *ngIf="tab === 'single'">
    <form class="lookup card" (submit)="$event.preventDefault(); generate()">
      <label for="classification-flow-barcode">Barcode</label>
      <div class="lookup-row">
        <input
          id="classification-flow-barcode"
          class="barcode-input"
          data-testid="classification-flow-barcode"
          name="barcode"
          type="text"
          inputmode="numeric"
          dir="ltr"
          [(ngModel)]="barcode"
          placeholder="7290002564928"
          [disabled]="running"
        />
        <button
          type="submit"
          data-testid="classification-flow-generate"
          [disabled]="running || picking !== null"
        >
          Generate
        </button>
        <button
          type="button"
          class="secondary"
          data-testid="classification-flow-pick-gold"
          [disabled]="running || picking !== null"
          (click)="pickSample(true)"
        >
          {{ picking === 'gold' ? 'Picking…' : 'With gold set' }}
        </button>
        <button
          type="button"
          class="secondary"
          data-testid="classification-flow-pick-plain"
          [disabled]="running || picking !== null"
          (click)="pickSample(false)"
        >
          {{ picking === 'plain' ? 'Picking…' : 'Without gold set' }}
        </button>
        <button
          type="button"
          class="secondary"
          data-testid="classification-flow-cancel"
          *ngIf="running"
          (click)="cancel()"
        >
          Cancel
        </button>
      </div>
    </form>

    <section
      class="card product-card"
      *ngIf="productId || productLabel"
      aria-label="מוצר"
      data-testid="classification-flow-product"
    >
      <h2>מוצר</h2>
      <div class="product-hero" *ngIf="hasImage && productId">
        <div
          class="product-photo-wrap"
          data-testid="classification-flow-product-image-wrap"
          tabindex="0"
          (focus)="openImagePopup()"
          (blur)="scheduleCloseImagePopup()"
        >
          <img
            class="product-photo"
            data-testid="classification-flow-product-image"
            [src]="productImageUrl(productId)"
            [alt]="productLabel"
            (mouseenter)="openImagePopup()"
            (mouseleave)="scheduleCloseImagePopup()"
          />
          <div
            class="product-photo-popup"
            *ngIf="imagePopupOpen"
            data-testid="classification-flow-product-image-popup"
            role="dialog"
            aria-label="תמונת מוצר מוגדלת"
          >
            <button
              type="button"
              class="product-photo-close"
              data-testid="classification-flow-product-image-close"
              aria-label="סגור תמונה"
              (mouseenter)="cancelCloseImagePopup()"
              (mouseleave)="scheduleCloseImagePopup()"
              (click)="closeImagePopup($event)"
            >
              ×
            </button>
            <img
              class="product-photo-popup-img"
              [src]="productImageUrl(productId)"
              alt=""
            />
          </div>
        </div>
        <p class="image-credit muted">העבירו עכבר להגדלה</p>
      </div>
      <p class="product-name" dir="auto" data-testid="classification-flow-product-name">
        {{ productLabel || '—' }}
      </p>
      <p class="muted" dir="auto" data-testid="classification-flow-product-brand">
        מותג: {{ brandName || '—' }}
      </p>
      <p class="muted" dir="auto" data-testid="classification-flow-product-qty">
        כמות: {{ qtyLabel || '—' }}
      </p>
      <p class="muted" *ngIf="barcode" dir="auto">ברקוד: {{ barcode }} · מזהה: {{ productId || '—' }}</p>
    </section>

    <ol class="timeline" *ngIf="phases.length > 0" aria-label="Flow steps">
      <li
        class="step phase"
        *ngFor="let phase of phases; let i = index"
        [attr.data-status]="phase.status"
        [attr.data-decision]="phase.decision"
      >
        <div class="step-index" aria-hidden="true">{{ i + 1 }}</div>
        <div class="step-body">
          <div class="step-top">
            <strong dir="ltr">{{ phase.title }}</strong>
            <span class="pill" [attr.data-status]="phase.status">{{ phase.status }}</span>
            <span class="pill decision" *ngIf="phase.decision" dir="ltr">{{ phase.decision }}</span>
          </div>
          <p class="subtitle" dir="ltr">{{ phase.subtitle }}</p>
          <p class="why" *ngIf="phase.why">
            <span class="why-label">Reason</span>
            {{ phase.why }}
          </p>
          <p class="muted meta" *ngIf="phase.model || phase.costUsd" dir="ltr">
            <span *ngIf="phase.model">{{ phase.model }}</span>
            <span *ngIf="phase.costUsd || phase.tokensIn || phase.tokensOut"> · ${{ phase.costUsd | number: '1.2-4' }}</span>
            <span *ngIf="phase.tokensIn || phase.tokensOut">
              · {{ phase.tokensIn }}/{{ phase.tokensOut }} tok
            </span>
          </p>

          <ol class="substeps" *ngIf="phase.children.length > 0" aria-label="Sub-steps">
            <li
              class="substep"
              *ngFor="let child of phase.children; let j = index"
              [attr.data-status]="child.status"
            >
              <div class="substep-index" aria-hidden="true">{{ i + 1 }}.{{ j + 1 }}</div>
              <div class="substep-body">
                <div class="step-top">
                  <strong dir="ltr">{{ subStepTitle(child.step) }}</strong>
                  <span class="pill" [attr.data-status]="child.status">{{ child.status }}</span>
                  <span class="pill decision" *ngIf="child.decision" dir="ltr">{{ child.decision }}</span>
                </div>
                <p class="subtitle" dir="ltr">{{ subStepSubtitle(child.step) }}</p>
                <p class="why" *ngIf="child.why">
                  <span class="why-label">Reason</span>
                  {{ child.why }}
                </p>
                <p class="muted meta" *ngIf="child.costUsd || child.tokensIn || child.tokensOut" dir="ltr">
                  <span *ngIf="child.costUsd || child.tokensIn || child.tokensOut">${{ child.costUsd | number: '1.2-4' }}</span>
                  <span *ngIf="child.tokensIn || child.tokensOut">
                    · {{ child.tokensIn }}/{{ child.tokensOut }} tok
                  </span>
                </p>
                <ng-container *ngIf="child.input || child.output">
                  <button
                    type="button"
                    class="linkish"
                    data-testid="classification-flow-step-io"
                    (click)="toggleIo(ioKey(child))"
                    [attr.aria-expanded]="isIoOpen(ioKey(child))"
                  >
                    {{ isIoOpen(ioKey(child)) ? 'Hide input / output' : 'Input / output' }}
                  </button>
                  <div class="io-panel" *ngIf="isIoOpen(ioKey(child))" dir="ltr">
                    <div class="io-block" *ngIf="child.input != null">
                      <h4>Input</h4>
                      <ng-container *ngFor="let block of prettyIo(child.input)">
                        <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
                        <pre
                          [class.json]="block.kind === 'json'"
                          [class.prompt]="block.kind === 'prompt'"
                          [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
                        >{{ block.text }}</pre>
                      </ng-container>
                    </div>
                    <div class="io-block" *ngIf="child.output != null">
                      <h4>Output</h4>
                      <ng-container *ngFor="let block of prettyIo(child.output)">
                        <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
                        <pre
                          [class.json]="block.kind === 'json'"
                          [class.prompt]="block.kind === 'prompt'"
                          [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
                        >{{ block.text }}</pre>
                      </ng-container>
                    </div>
                  </div>
                </ng-container>
              </div>
            </li>
          </ol>

          <ng-container *ngIf="phase.children.length === 0 && phase.event && (phase.event.input || phase.event.output)">
            <button
              type="button"
              class="linkish"
              data-testid="classification-flow-phase-io"
              (click)="toggleIo(phaseIoKey(phase))"
              [attr.aria-expanded]="isIoOpen(phaseIoKey(phase))"
            >
              {{ isIoOpen(phaseIoKey(phase)) ? 'Hide input / output' : 'Input / output' }}
            </button>
            <div class="io-panel" *ngIf="isIoOpen(phaseIoKey(phase))" dir="ltr">
              <div class="io-block" *ngIf="phase.event.input != null">
                <h4>Input</h4>
                <ng-container *ngFor="let block of prettyIo(phase.event.input)">
                  <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
                  <pre
                    [class.json]="block.kind === 'json'"
                    [class.prompt]="block.kind === 'prompt'"
                    [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
                  >{{ block.text }}</pre>
                </ng-container>
              </div>
              <div class="io-block" *ngIf="phase.event.output != null">
                <h4>Output</h4>
                <ng-container *ngFor="let block of prettyIo(phase.event.output)">
                  <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
                  <pre
                    [class.json]="block.kind === 'json'"
                    [class.prompt]="block.kind === 'prompt'"
                    [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
                  >{{ block.text }}</pre>
                </ng-container>
              </div>
            </div>
          </ng-container>
        </div>
      </li>
    </ol>

    <section
      class="card result"
      *ngIf="score as sc"
      id="classification-flow-conclusion"
      aria-label="Result"
    >
      <p class="result-kicker">Result</p>
      <div
        class="result-banner"
        dir="ltr"
        [attr.data-save]="showsPair(sc)"
        [attr.data-match]="sc.match"
      >
        <p class="result-action">{{ resultAction(sc) }}</p>
        <ng-container
          *ngIf="showsPair(sc)"
          [ngTemplateOutlet]="taxPath"
          [ngTemplateOutletContext]="{ levels: taxonomyLevels(sc.predictedCategory, sc.predictedType), size: 'hero' }"
        ></ng-container>
        <p class="result-match" *ngIf="sc.goldCategory">{{ matchLabel(sc.match, true) }}</p>
      </div>

      <div class="result-compare" *ngIf="sc.goldCategory" dir="ltr">
        <div>
          <span>Predicted</span>
          <ng-container
            [ngTemplateOutlet]="taxPath"
            [ngTemplateOutletContext]="{ levels: taxonomyLevels(sc.predictedCategory, sc.predictedType), size: 'compact' }"
          ></ng-container>
        </div>
        <div>
          <span>Gold</span>
          <ng-container
            [ngTemplateOutlet]="taxPath"
            [ngTemplateOutletContext]="{ levels: taxonomyLevels(sc.goldCategory, sc.goldType), size: 'compact' }"
          ></ng-container>
        </div>
      </div>

      <ul class="vote-list" *ngIf="scoreVotes(sc).length > 0" data-testid="classification-flow-model-compare">
        <li class="vote-card" *ngFor="let v of scoreVotes(sc)" [attr.data-match]="v.matchGold" [attr.data-tone]="voteTone(v)">
          <div class="vote-head">
            <span class="vote-model">{{ v.model }}</span>
            <span class="pill" [attr.data-tone]="voteTone(v)" [attr.data-match]="v.matchGold">{{ voteResultLabel(v) }}</span>
          </div>
          <ng-container
            [ngTemplateOutlet]="taxPath"
            [ngTemplateOutletContext]="{ levels: voteLevels(v), size: 'compact' }"
          ></ng-container>
          <div class="vote-note" *ngIf="v.categoryEvidence">
            <span class="vote-note-label">Group</span>
            <span class="vote-note-text" dir="auto">{{ v.categoryEvidence }}</span>
          </div>
          <div class="vote-note" *ngIf="showTypeEvidence(v)">
            <span class="vote-note-label">Sub-group</span>
            <span class="vote-note-text" dir="auto">{{ v.typeEvidence }}</span>
          </div>
        </li>
      </ul>

      <p class="muted result-cost" dir="ltr">Cost ${{ sc.costUsd | number: '1.2-4' }}</p>
      <button
        type="button"
        class="linkish"
        (click)="toggleIo('score')"
        [attr.aria-expanded]="isIoOpen('score')"
      >
        {{ isIoOpen('score') ? 'Hide score details' : 'Score details' }}
      </button>
      <div class="io-panel" *ngIf="isIoOpen('score')" dir="ltr">
        <div class="io-block" *ngIf="sc.input != null">
          <h4>Input</h4>
          <ng-container *ngFor="let block of prettyIo(sc.input)">
            <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
            <pre
              [class.json]="block.kind === 'json'"
              [class.prompt]="block.kind === 'prompt'"
              [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
            >{{ block.text }}</pre>
          </ng-container>
        </div>
        <div class="io-block" *ngIf="sc.output != null">
          <h4>Output</h4>
          <ng-container *ngFor="let block of prettyIo(sc.output)">
            <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
            <pre
              [class.json]="block.kind === 'json'"
              [class.prompt]="block.kind === 'prompt'"
              [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
            >{{ block.text }}</pre>
          </ng-container>
        </div>
        <div class="io-block" *ngIf="sc.input == null && sc.output == null">
          <ng-container *ngFor="let block of prettyIo(sc)">
            <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
            <pre
              [class.json]="block.kind === 'json'"
              [class.prompt]="block.kind === 'prompt'"
              [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
            >{{ block.text }}</pre>
          </ng-container>
        </div>
      </div>
    </section>
  </ng-container>

  <ng-container *ngIf="tab === 'scoreboard'">
    <form class="lookup card" (submit)="$event.preventDefault(); runScoreboard()">
      <label for="classification-flow-limit">How many gold (admin-verified) products?</label>
      <label for="classification-flow-scoreboard-model">Model</label>
      <div class="lookup-row">
        <select
          id="classification-flow-scoreboard-model"
          class="barcode-input"
          data-testid="classification-flow-scoreboard-model"
          name="scoreboardModel"
          [(ngModel)]="scoreboardModel"
          [disabled]="running"
        >
          <option value="">Voting companies</option>
          <option *ngFor="let model of scoreboardChoices" [value]="model.id">
            {{ model.label }}
          </option>
        </select>
        <input
          id="classification-flow-limit"
          class="limit-input"
          data-testid="classification-flow-limit"
          type="number"
          min="1"
          max="100"
          [(ngModel)]="scoreboardLimit"
          name="limit"
          [disabled]="running"
        />
        <button
          type="submit"
          data-testid="classification-flow-scoreboard-run"
          [disabled]="running"
        >
          Run scoreboard
        </button>
        <button
          type="button"
          class="secondary"
          *ngIf="running"
          (click)="cancel()"
        >
          Cancel
        </button>
      </div>
      <p class="muted" *ngIf="scoreboardModel">
        One model, text only. The voting seats stay as they are.
      </p>
    </form>

    <section class="card summary" *ngIf="scoreboardSummary as sum">
      <h2>Summary</h2>
      <p>{{ sum.why }}</p>
      <div class="io-panel summary-io" *ngIf="sum.output">
        <ng-container *ngFor="let block of prettyIo(sum.output)">
          <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
          <pre
            [class.json]="block.kind === 'json'"
            [class.prompt]="block.kind === 'prompt'"
            [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
          >{{ block.text }}</pre>
        </ng-container>
      </div>
    </section>

    <div class="table-wrap" *ngIf="scoreboardRows.length > 0">
      <table class="score-table">
        <thead>
          <tr>
            <th>Barcode</th>
            <th>Name</th>
            <th>Predicted</th>
            <th>Gold</th>
            <th>Score</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of scoreboardRows" [attr.data-match]="row.match">
            <td dir="ltr">{{ row.barcode }}</td>
            <td dir="auto">{{ row.displayName }}</td>
            <td>
              <ng-container
                [ngTemplateOutlet]="taxPath"
                [ngTemplateOutletContext]="{ levels: taxonomyLevels(row.predictedCategory, row.predictedType), size: 'mini' }"
              ></ng-container>
            </td>
            <td>
              <ng-container
                [ngTemplateOutlet]="taxPath"
                [ngTemplateOutletContext]="{ levels: taxonomyLevels(row.goldCategory, row.goldType), size: 'mini' }"
              ></ng-container>
            </td>
            <td>
              <span class="pill" [attr.data-match]="row.match" dir="ltr">
                {{ row.decision }}
              </span>
            </td>
            <td dir="ltr">${{ row.costUsd | number: '1.2-4' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </ng-container>

  <ng-container *ngIf="tab === 'unlabeled'">

    <form class="lookup card" (submit)="$event.preventDefault(); startBatch()">
      <label for="classification-flow-batch-limit">How many unlabeled products?</label>
      <div class="lookup-row">
        <input
          id="classification-flow-batch-limit"
          class="limit-input"
          data-testid="classification-flow-batch-limit"
          type="number"
          min="1"
          max="100"
          name="batchLimit"
          [(ngModel)]="batchLimit"
          [disabled]="batchStarting"
        />
        <button
          type="submit"
          data-testid="classification-flow-batch-run"
          [disabled]="batchStarting"
        >
          Run
        </button>
      </div>
      <label class="batch-api-option">
        <input
          type="checkbox"
          name="batchApi"
          data-testid="classification-flow-batch-api"
          [(ngModel)]="batchApi"
          [disabled]="batchStarting"
        />
        Batch API
      </label>
      <p class="muted">Products with no admin label and no saved group or sub-group. Ones with a photo and a storefront name come first. The run keeps going if you leave this page.</p>
      <p class="muted" *ngIf="batchApi">Half the token price. Answers come back later, often within a few hours. The group is sent first, then the sub-group. GLM is free, so it is called live. DeepSeek has no batch API, so it is omitted from this route.</p>
    </form>

    <section class="card" *ngIf="batches.length > 0" aria-label="Recent batches">
      <h2>Recent batches</h2>
      <ul class="batch-recent">
        <li *ngFor="let row of batches">
          <button
            type="button"
            class="batch-recent-btn"
            [class.active]="batch?.summary?.id === row.id"
            (click)="openBatch(row.id)"
          >
            <span dir="ltr">{{ row.createdAt | date: 'MMM d, HH:mm' }}</span>
            <span>{{ row.itemCount }} of {{ row.limitN }}</span>
            <span>{{ row.savedCount }} saved</span>
            <span *ngIf="row.route === 'batch'">batch API</span>
            <span dir="ltr">${{ row.costUsd | number: '1.2-4' }}</span>
            <span class="muted" *ngIf="!row.finishedAt">running</span>
          </button>
        </li>
      </ul>
    </section>

    <section class="card" *ngIf="batch as current" aria-label="Batch progress">
      <p class="result-kicker">Batch</p>
      <p class="batch-progress" dir="ltr">
        {{ current.summary.doneCount }} / {{ current.summary.itemCount || current.summary.limitN }}
        · {{ current.summary.savedCount }} saved
        · {{ current.summary.blankCount }} left blank
        · {{ current.summary.errorCount }} errors
        · ${{ current.summary.costUsd | number: '1.2-4' }}
        <span *ngIf="current.summary.route === 'batch' && !current.summary.finishedAt && current.summary.phase !== 'category_submit' && current.summary.phase !== 'category_wait'">
          · {{ batchPhaseLabel(current.summary.phase) }}
        </span>
      </p>
      <p class="batch-category" *ngIf="categoryPlanLine() as categoryLine">{{ categoryLine }}</p>
      <p class="error" *ngIf="current.summary.note">{{ current.summary.note }}</p>
      <p class="muted" *ngIf="current.summary.itemCount === 0">No unlabeled products left to run.</p>
      <p class="muted" *ngIf="current.summary.finishedAt && current.summary.itemCount < current.summary.limitN && current.summary.itemCount > 0">
        Found {{ current.summary.itemCount }} of {{ current.summary.limitN }} unlabeled products.
      </p>
      <div class="batch-filters" role="tablist">
        <button type="button" [class.active]="batchFilter === 'all'" (click)="batchFilter = 'all'">All</button>
        <button type="button" [class.active]="batchFilter === 'saved'" (click)="batchFilter = 'saved'">Saved</button>
        <button type="button" [class.active]="batchFilter === 'blank'" (click)="batchFilter = 'blank'">Left blank</button>
        <button type="button" [class.active]="batchFilter === 'error'" (click)="batchFilter = 'error'">Error</button>
      </div>
      <ul class="batch-cards">
        <li
          class="run-card"
          *ngFor="let card of filteredBatchCards(); let i = index"
          [attr.data-outcome]="cardPillOutcome(card)"
          [class.open]="openProductId === card.productId"
        >
          <span class="run-card-index" [attr.aria-label]="'Product ' + (i + 1)">{{ i + 1 }}</span>
          <div class="run-head" [class.no-photo]="!card.hasImage">
            <div
              class="product-photo-wrap run-photo"
              *ngIf="card.hasImage"
              data-testid="classification-flow-batch-image-wrap"
              tabindex="0"
              (focus)="openCardImagePopup(card.productId)"
              (blur)="scheduleCloseImagePopup()"
            >
              <img
                class="product-photo"
                data-testid="classification-flow-batch-image"
                [src]="productImageUrl(card.productId)"
                [alt]="card.displayName"
                (mouseenter)="openCardImagePopup(card.productId)"
                (mouseleave)="scheduleCloseImagePopup()"
              />
              <div
                class="product-photo-popup"
                *ngIf="imagePopupProductId === card.productId"
                data-testid="classification-flow-batch-image-popup"
                role="dialog"
                aria-label="תמונת מוצר מוגדלת"
              >
                <button
                  type="button"
                  class="product-photo-close"
                  aria-label="סגור תמונה"
                  (mouseenter)="cancelCloseImagePopup()"
                  (mouseleave)="scheduleCloseImagePopup()"
                  (click)="closeImagePopup($event)"
                >
                  ×
                </button>
                <img
                  class="product-photo-popup-img"
                  [src]="productImageUrl(card.productId)"
                  alt=""
                />
              </div>
            </div>
          <button
            type="button"
            class="run-toggle"
            [attr.aria-expanded]="openProductId === card.productId"
            (click)="toggleBatchCard(card)"
          >
            <span class="run-main">
              <span class="run-kicker">
                <span class="pill" [attr.data-outcome]="cardPillOutcome(card)">{{ batchCardAction(card) }}</span>
                <span class="run-kicker-end">
                  <span class="run-cost" *ngIf="card.costUsd">${{ card.costUsd | number: '1.2-4' }}</span>
                  <span class="run-chevron" aria-hidden="true"></span>
                </span>
              </span>
              <div class="run-pair" *ngIf="cardLevels(card).length">
                <ng-container
                  [ngTemplateOutlet]="taxPath"
                  [ngTemplateOutletContext]="{ levels: cardLevels(card), size: 'compact' }"
                ></ng-container>
              </div>
              <span class="run-name" dir="auto">{{ card.displayName }}</span>
              <span class="run-meta" dir="auto" data-testid="classification-flow-batch-brand">
                מותג: {{ card.brandName || '—' }}
              </span>
              <span class="run-meta" dir="auto" data-testid="classification-flow-batch-qty">
                כמות: {{ card.qtyLabel || '—' }}
              </span>
              <span class="run-barcode">{{ card.barcode }}</span>
              <span class="run-crosswalk" *ngIf="cardCrosswalk(card) as cw" dir="auto" title="Storefront aisle crosswalk">
                <span class="crosswalk-badge">Crosswalk</span>
                <span class="crosswalk-text">{{ cw }}</span>
              </span>
              <span class="run-why" *ngIf="categoryWhy(card) as why">{{ why }}</span>
              <span class="run-error" *ngIf="cardError(card) as message">{{ message }}</span>
            </span>
          </button>
          <div class="run-head-actions">
            <button
              type="button"
              class="rerun-btn-icon"
              [class.is-rerunning]="rerunningProductId === card.productId"
              title="Rerun classification"
              aria-label="Rerun classification"
              [disabled]="rerunningProductId === card.productId || card.status === 'running'"
              (click)="rerunCard(card, $event)"
            >
              <span *ngIf="rerunningProductId === card.productId" class="rerun-in-progress">
                <span class="spinner" aria-hidden="true"></span>
                <span>Rerunning…</span>
              </span>
              <span *ngIf="rerunningProductId !== card.productId">↻ Rerun</span>
            </button>
          </div>
          </div>
          <ul class="model-lines" *ngIf="cardModelRows(card).length" aria-label="Model conclusions">
            <li *ngFor="let row of cardModelRows(card)" [attr.data-tone]="row.tone">
              <span class="model-lines-name">{{ row.name }}</span>
              <span class="model-lines-body">
                <span class="model-lines-conclusion" *ngIf="row.conclusion" dir="auto">{{ row.conclusion }}</span>
                <ng-container
                  *ngIf="row.levels.length"
                  [ngTemplateOutlet]="taxPath"
                  [ngTemplateOutletContext]="{ levels: row.levels, size: 'mini' }"
                ></ng-container>
                <span class="model-lines-time" *ngFor="let line of row.callSegments">
                  <ng-container *ngIf="line.isAisle">Group from the aisle.</ng-container>
                  <ng-container *ngIf="!line.isAisle && line.notSent">{{ line.step }} not sent.</ng-container>
                  <ng-container *ngIf="!line.isAisle && !line.notSent">
                    <ng-container *ngIf="line.sentClock">
                      {{ line.step }}
                      <button
                        type="button"
                        class="call-time-link"
                        (click)="openCallIo(card, line.call, 'prompt', $event)"
                        title="View prompt input"
                      >sent {{ line.sentClock }}</button><ng-container *ngIf="line.receivedClock">, <button
                        type="button"
                        class="call-time-link"
                        (click)="openCallIo(card, line.call, 'output', $event)"
                        title="View LLM output"
                      >received {{ line.receivedClock }}</button>.</ng-container><ng-container *ngIf="!line.receivedClock && !line.answered">, not back yet.</ng-container><ng-container *ngIf="!line.receivedClock && line.answered">, <button
                        type="button"
                        class="call-time-link"
                        (click)="openCallIo(card, line.call, 'output', $event)"
                        title="View LLM output"
                      >received</button>, time not recorded.</ng-container>
                    </ng-container>
                    <ng-container *ngIf="!line.sentClock && line.receivedClock">
                      {{ line.step }}
                      <button
                        type="button"
                        class="call-time-link"
                        (click)="openCallIo(card, line.call, 'output', $event)"
                        title="View LLM output"
                      >received {{ line.receivedClock }}</button>.
                    </ng-container>
                    <ng-container *ngIf="!line.sentClock && !line.receivedClock && line.answered">
                      {{ line.step }}
                      <button
                        type="button"
                        class="call-time-link"
                        (click)="openCallIo(card, line.call, 'output', $event)"
                        title="View LLM output"
                      >received</button>, time not recorded.
                    </ng-container>
                    <ng-container *ngIf="!line.sentClock && !line.receivedClock && !line.answered">
                      {{ line.step }} not back yet.
                    </ng-container>
                  </ng-container>
                </span>
              </span>
              <span class="model-lines-action">
                <button
                  type="button"
                  class="model-approve"
                  *ngIf="row.canApprove"
                  [disabled]="approvingKey === row.key"
                  (click)="approveModel(card, row.model, $event)"
                >{{ approvingKey === row.key ? 'Saving…' : 'Approve' }}</button>
                <span class="model-saved" *ngIf="row.saved">Saved</span>
              </span>
            </li>
          </ul>
          <div class="run-detail" *ngIf="openProductId === card.productId">
            <div class="rerun-progress-banner" *ngIf="rerunningProductId === card.productId">
              <span class="spinner" aria-hidden="true"></span>
              <span>Rerunning classification models… Updating consensus proposals and step trace.</span>
            </div>
            <div class="gathered" *ngIf="card.gathered?.length">
              <p class="section-label">Gathered</p>
              <div class="gathered-line" *ngFor="let line of card.gathered">
                <span class="gathered-label">{{ line.label }}</span>
                <span class="gathered-text" dir="auto">{{ line.text }}</span>
              </div>
            </div>
            <div class="detail-votes" *ngIf="openVoteNotes(card).length">
              <p class="section-label">Why</p>
              <ul class="vote-list">
                <li class="vote-card" *ngFor="let v of openVoteNotes(card)" [attr.data-tone]="voteTone(v)">
                  <div class="vote-head">
                    <span class="vote-model">{{ modelShortName(v.model) }}</span>
                  </div>
                  <ng-container
                    [ngTemplateOutlet]="taxPath"
                    [ngTemplateOutletContext]="{ levels: voteLevels(v), size: 'compact' }"
                  ></ng-container>
                  <div class="vote-note" *ngIf="v.categoryEvidence">
                    <span class="vote-note-label">Group</span>
                    <span class="vote-note-text" dir="auto">{{ v.categoryEvidence }}</span>
                  </div>
                  <div class="vote-note" *ngIf="showTypeEvidence(v)">
                    <span class="vote-note-label">Sub-group</span>
                    <span class="vote-note-text" dir="auto">{{ v.typeEvidence }}</span>
                  </div>
                </li>
              </ul>
            </div>
            <p class="muted" *ngIf="batchDetailLoading">Loading this product…</p>
            <ng-container *ngIf="batchDetail as detail">
              <div class="trace" *ngIf="detail.steps.length > 0">
                <p class="section-label">Steps</p>
                <ol class="trace-steps">
                  <li class="trace-step" *ngFor="let step of detail.steps; let idx = index">
                    <div class="trace-step-top">
                      <strong>{{ batchStepTitle(step) }}</strong>
                      <span class="trace-step-meta">
                        <span class="trace-cost" dir="ltr" *ngIf="step.costUsd">${{ step.costUsd | number: '1.2-4' }}</span>
                        <span class="pill" *ngIf="step.decision">{{ step.decision }}</span>
                      </span>
                    </div>
                    <p class="subtitle" dir="ltr" *ngIf="batchStepSubtitle(step)">{{ batchStepSubtitle(step) }}</p>
                    <p class="why" dir="auto" *ngIf="batchStepReason(step) as reason">
                      <span class="why-label">Reason</span>
                      {{ reason }}
                    </p>
                    <p class="muted meta" *ngIf="step.tokensIn || step.tokensOut" dir="ltr">
                      {{ step.tokensIn }}/{{ step.tokensOut }} tok
                    </p>
                    <ng-container *ngIf="step.llmInput || step.llmOutput">
                      <button
                        type="button"
                        class="linkish"
                        data-testid="classification-flow-batch-step-io"
                        (click)="toggleBatchStepIo(batchStepIoKey(detail.productId, step, idx))"
                        [attr.aria-expanded]="isBatchStepIoOpen(batchStepIoKey(detail.productId, step, idx))"
                      >
                        {{ isBatchStepIoOpen(batchStepIoKey(detail.productId, step, idx)) ? 'Hide input / output' : 'Input / output' }}
                      </button>
                      <div class="io-panel" *ngIf="isBatchStepIoOpen(batchStepIoKey(detail.productId, step, idx))" dir="ltr">
                        <div class="io-block" *ngIf="step.llmInput != null">
                          <h4>Input</h4>
                          <ng-container *ngFor="let block of prettyIo(step.llmInput)">
                            <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
                            <pre
                              [class.json]="block.kind === 'json'"
                              [class.prompt]="block.kind === 'prompt'"
                              [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
                            >{{ block.text }}</pre>
                          </ng-container>
                        </div>
                        <div class="io-block" *ngIf="step.llmOutput != null">
                          <h4>Output</h4>
                          <ng-container *ngFor="let block of prettyIo(step.llmOutput)">
                            <p class="io-label" *ngIf="block.label" dir="ltr">{{ block.label }}</p>
                            <pre
                              [class.json]="block.kind === 'json'"
                              [class.prompt]="block.kind === 'prompt'"
                              [attr.dir]="block.kind === 'prompt' ? 'auto' : 'ltr'"
                            >{{ block.text }}</pre>
                          </ng-container>
                        </div>
                      </div>
                    </ng-container>
                  </li>
                </ol>
              </div>
            </ng-container>
          </div>
        </li>
      </ul>
    </section>
  </ng-container>

  <div
    class="call-io-popup"
    *ngIf="callIoPopup as io"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="io.modelName + ' ' + io.stepTitle + ' prompt and output'"
    data-testid="classification-call-io-popup"
  >
    <button
      type="button"
      class="call-io-backdrop"
      aria-label="Close"
      (click)="closeCallIo()"
    ></button>
    <div class="call-io-card">
      <button
        type="button"
        class="call-io-close"
        data-testid="classification-call-io-close"
        aria-label="Close"
        (click)="closeCallIo()"
      >
        ×
      </button>

      <div class="call-io-header">
        <div class="call-io-header-title-row">
          <span class="call-io-badge">{{ io.modelName }}</span>
          <span class="call-io-badge call-io-badge-wave">{{ io.stepTitle }}</span>
          <h3 dir="auto">{{ io.productName }}</h3>
        </div>
        <p class="call-io-subtitle" dir="ltr">
          <span *ngIf="io.barcode">Barcode: {{ io.barcode }}</span>
          <span *ngIf="io.sentAt"> · Sent: {{ formatCallClock(io.sentAt) }}</span>
          <span *ngIf="io.receivedAt"> · Received: {{ formatCallClock(io.receivedAt) }}</span>
          <span *ngIf="!io.receivedAt && !io.answered"> · Waiting for response</span>
        </p>
      </div>

      <div class="call-io-tabs-bar">
        <div class="call-io-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="call-io-tab-btn"
            [class.active]="callIoTab === 'prompt'"
            [attr.aria-selected]="callIoTab === 'prompt'"
            (click)="callIoTab = 'prompt'"
          >
            Prompt Input
          </button>
          <button
            type="button"
            role="tab"
            class="call-io-tab-btn"
            [class.active]="callIoTab === 'output'"
            [attr.aria-selected]="callIoTab === 'output'"
            (click)="callIoTab = 'output'"
          >
            LLM Output
            <span class="call-io-tab-dot" *ngIf="!io.receivedAt && !io.answered" title="Waiting for response">⏳</span>
          </button>
        </div>

        <button
          type="button"
          class="call-io-copy-btn"
          [disabled]="callIoLoading || (callIoTab === 'prompt' ? !io.prompt : !io.response)"
          (click)="copyCallIoText(callIoTab === 'prompt' ? io.prompt : io.response)"
        >
          {{ callIoCopied ? '✓ Copied' : 'Copy ' + (callIoTab === 'prompt' ? 'Prompt' : 'Output') }}
        </button>
      </div>

      <div class="call-io-body">
        <div class="call-io-loading" *ngIf="callIoLoading">
          <span class="spinner" aria-hidden="true"></span>
          <span>Loading prompt and output…</span>
        </div>

        <div class="call-io-error" *ngIf="!callIoLoading && io.error">
          {{ io.error }}
        </div>

        <ng-container *ngIf="!callIoLoading">
          <div *ngIf="callIoTab === 'prompt'" class="call-io-pane">
            <pre class="call-io-block" dir="auto" *ngIf="io.prompt">{{ io.prompt }}</pre>
            <p class="call-io-empty muted" *ngIf="!io.prompt">No prompt input was recorded for this call.</p>
          </div>

          <div *ngIf="callIoTab === 'output'" class="call-io-pane">
            <pre class="call-io-block" dir="auto" *ngIf="io.response">{{ io.response }}</pre>
            <div class="call-io-pending" *ngIf="!io.response && !io.answered">
              <span class="spinner" aria-hidden="true"></span>
              <p>Waiting for LLM response…</p>
              <p class="muted" *ngIf="io.sentAt">Sent at {{ formatCallClock(io.sentAt) }}. Response will appear once the batch job completes.</p>
            </div>
            <p class="call-io-empty muted" *ngIf="!io.response && io.answered">No text response recorded for this call.</p>
          </div>
        </ng-container>
      </div>
    </div>
  </div>

  <ng-template #taxPath let-levels="levels" let-size="size">
    <p class="tax-empty" *ngIf="levels.length === 0">—</p>
    <ol class="tax-path" *ngIf="levels.length > 0" [attr.data-size]="size || 'compact'" [attr.data-count]="levels.length">
      <li *ngFor="let level of levels">
        <span class="tax-label">{{ level.label }}</span>
        <span class="tax-name" dir="auto">{{ level.name }}</span>
        <span class="tax-code" *ngIf="level.code" dir="ltr">{{ level.code }}</span>
      </li>
    </ol>
  </ng-template>
</div>
